import { useCallback, useState } from "react";
import { Check, X, Pencil } from "lucide-react";
import { api, type PersonInferredRelationship, type RelationshipType } from "../lib/api";
import { useRelationshipTaxonomy } from "../lib/relationship-taxonomy";

/** One-tap relationship-confirmation pill.
 *
 *  596 dotted relationships sat unconfirmed because the only place to
 *  confirm was a /explore/relationships page the user never visited.
 *  This pill moves the gesture inline to the surfaces the user is
 *  already on (Person card, Engagement card, Recall footer).
 *
 *  States:
 *    - inferred (confirmed=0): "Memso assumes <name> is your <role>"
 *      with one-tap Confirm / Reject / Edit-role.
 *    - auto_confirmed: already ranking; show a quiet transparency
 *      footer "Memso assumed this" with the same correct/Edit/reject
 *      affordances so the user can still steer it.
 *    - user-confirmed (any other source): render nothing — the user
 *      already decided; don't nag.
 *
 *  Acting optimistically removes the pill; the optional onChanged
 *  lets the host refetch to lock canonical state. */
export function InlineRelationshipConfirm({
  relationship,
  personName,
  onChanged,
}: {
  relationship: PersonInferredRelationship;
  personName: string;
  onChanged?: () => void;
}) {
  const [done, setDone] = useState<null | "confirmed" | "rejected" | "edited">(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const taxo = useRelationshipTaxonomy();

  const inferred = !relationship.confirmed;
  const autoConfirmed =
    relationship.confirmed && relationship.inference_source === "auto_confirmed";
  // A low-confidence more-specific guess sitting under the committed
  // type (e.g. type=spouse, inferred_subtype=wife). Offer it as the
  // pre-selected option so one tap promotes it.
  const subtypeGuess =
    relationship.inferred_subtype &&
    relationship.inferred_subtype !== relationship.type
      ? relationship.inferred_subtype
      : null;

  const confirm = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.relationshipConfirm(relationship.id);
      setDone("confirmed");
      onChanged?.();
    } finally {
      setBusy(false);
    }
  }, [busy, relationship.id, onChanged]);

  const reject = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.relationshipReject(relationship.id);
      setDone("rejected");
      onChanged?.();
    } finally {
      setBusy(false);
    }
  }, [busy, relationship.id, onChanged]);

  const saveEdit = useCallback(
    async (newType: string) => {
      if (busy || !newType) return;
      setBusy(true);
      try {
        await api.relationshipEdit(relationship.id, newType as RelationshipType);
        setDone("edited");
        setEditing(false);
        onChanged?.();
      } finally {
        setBusy(false);
      }
    },
    [busy, relationship.id, onChanged],
  );

  // User already decided — stay out of the way.
  if (relationship.confirmed && !autoConfirmed) return null;
  if (done === "confirmed" || done === "edited") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-300">
        <Check className="w-3 h-3" /> Confirmed
      </span>
    );
  }
  if (done === "rejected") {
    return (
      <span className="text-[10px] font-semibold text-slate-400">
        Thanks — Memso will stop suggesting that.
      </span>
    );
  }

  const role = taxo.humanize(relationship.type, relationship.label_custom);
  const lead = subtypeGuess
    ? `Memso thinks ${personName} may be your ${taxo.humanize(subtypeGuess)} (filed under ${role} until confirmed).`
    : autoConfirmed
      ? `Memso assumed ${personName} is your ${role}.`
      : `Memso assumes ${personName} is your ${role}.`;

  if (editing) {
    const groups = taxo.roots.map((root) => ({
      root,
      options: taxo.userFacingDescendants
        ? taxo.userFacingDescendants(root.type)
        : [],
    }));
    return (
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="text-slate-500 dark:text-slate-400">Set role:</span>
        <select
          autoFocus
          disabled={busy}
          defaultValue={subtypeGuess ?? relationship.type}
          onChange={(e) => saveEdit(e.target.value)}
          className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-900 dark:text-slate-100"
        >
          {groups.length === 0 ? (
            <option value={relationship.type}>
              {taxo.humanize(relationship.type)}
            </option>
          ) : (
            groups.map(({ root, options }) => (
              <optgroup key={root.type} label={root.display_name}>
                {options.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.display_name}
                  </option>
                ))}
              </optgroup>
            ))
          )}
        </select>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          aria-label="Cancel edit"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
      <span>{lead}</span>
      <button
        type="button"
        onClick={confirm}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold px-1.5 py-0.5 disabled:opacity-50"
      >
        <Check className="w-3 h-3" />
        {autoConfirmed ? "Looks right" : "Confirm"}
      </button>
      <button
        type="button"
        onClick={() => setEditing(true)}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-[10px] font-semibold px-1.5 py-0.5 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
      >
        <Pencil className="w-3 h-3" />
        Edit role
      </button>
      <button
        type="button"
        onClick={reject}
        disabled={busy}
        className="inline-flex items-center text-[10px] font-semibold text-slate-500 hover:text-rose-600 dark:hover:text-rose-300 px-1 py-0.5 disabled:opacity-50"
        aria-label="Reject"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
